/**
 * The bars of the piece a run takes: the whole of it, or a first and a last, with the length
 * of the piece beside them.
 *
 * The whole piece is the default and a box, ticked, because arranging a piece means arranging
 * all of it; a window is the exception, asked for by clearing the box, and the fields keep
 * what was last typed in them for when it is. While the box is ticked they are shown greyed as
 * `1 – <length>`, so the reader sees what is about to be taken.
 *
 * The length is printed because a window cannot be chosen without it. It is the length of the
 * file as this browser read it, counted by position from bar 1, which is how the pipeline
 * numbers a window too (`lib/score-window.ts`). A last bar typed past it is ringed: the reader
 * clips it to the end, which is harmless, but a field saying 50 beside "of 19" reads as a
 * mistake, and it is one the reader should see before pressing Arrange.
 */

import { FIELD, LABEL } from "./Chrome";
import { count } from "../lib/format";

export interface ArrangeBarsProps {
  /** Whether the run takes every bar, whatever the fields say. */
  readonly whole: boolean;
  readonly first: string;
  readonly last: string;
  /** Bars in the opened piece, or `null` before one is opened or where it could not be read. */
  readonly total: number | null;
  onWhole(whole: boolean): void;
  onWindow(first: string, last: string): void;
}

export default function ArrangeBars({
  whole,
  first,
  last,
  total,
  onWhole,
  onWindow,
}: ArrangeBarsProps): JSX.Element {
  const end = total === null ? "end" : String(total);
  const past = !whole && total !== null && last.trim() !== "" && Number(last) > total;

  return (
    <div className="flex items-center gap-1.5">
      <label
        title="Arrange every bar of the piece. Clear it to arrange only the bars typed beside it."
        className="inline-flex h-7 cursor-pointer items-center gap-1.5 text-[11px] text-shell-text hover:text-shell-lit"
      >
        <input
          type="checkbox"
          checked={whole}
          onChange={(event) => onWhole(event.target.checked)}
          className="h-3.5 w-3.5 accent-shell-live"
        />
        Whole piece
      </label>
      <span className={LABEL}>Bars</span>
      <input
        value={whole ? "1" : first}
        onChange={(event) => onWindow(event.target.value, last)}
        disabled={whole}
        inputMode="numeric"
        placeholder="1"
        aria-label="First bar"
        className={`${FIELD} disabled:opacity-50`}
      />
      <span className="text-shell-dim">–</span>
      <input
        value={whole ? end : last}
        onChange={(event) => onWindow(first, event.target.value)}
        disabled={whole}
        inputMode="numeric"
        placeholder="end"
        aria-label="Last bar"
        title={past ? `Past the end: bars up to ${end} will be read.` : undefined}
        className={`${FIELD} disabled:opacity-50${past ? " ring-1 ring-fault" : ""}`}
      />
      {total === null ? null : (
        <span className="font-mono text-[11px] text-shell-dim">of {count(total)}</span>
      )}
    </div>
  );
}
